import React, { useRef, useState, useCallback, useEffect } from 'react';
import AdvancedOptions from './AdvancedOptions';
import { Link } from '@tanstack/react-router';
import { PresetSelector } from '../../../components/PresetSelector';
import prettyBytes from "pretty-bytes";
import { Button } from '../../ui';

const MAX_FILE_SIZE_MB = 500;

// Helper function for file validation
const validateFile = (file) => {
  const type = file.type !== ""
    ? file.type
    : file.name.endsWith(".glb")
      ? "model/gltf-binary"
      : file.name.endsWith(".gltf")
        ? "model/gltf+json"
        : "application/octet-stream";

  if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
    return `File "${file.name}" is too large. Max size: ${MAX_FILE_SIZE_MB}MB.`;
  }
  if (type !== "model/gltf+json" && type !== "model/gltf-binary") {
    return `File "${file.name}" is not a valid GLTF or GLB file.`;
  }
  return null; // No error
};

export default function FileUploadModal({
  fileError,
  setFileError,
  showAdvanced,
  setShowAdvanced,
  advancedOptions,
  setAdvancedOptions,
  onNestedChange,
  dragging,
  setDragging,
  closeModal,
  credits,
  selectedPreset,
  presets,
  onPresetSelect,
  presetsLoading,
  presetsError,
  onStartBatchUpload,
  initialFiles,
}) {
  const modalDropRef = useRef(null);
  const fileInputRef = useRef(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileValidationErrors, setFileValidationErrors] = useState({});

  const hasValidFiles = selectedFiles.length > 0 && Object.keys(fileValidationErrors).length === 0;
  const totalSelectedSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
  const creditsNeeded = selectedFiles.length;
  const hasEnoughCredits = credits === null || credits >= creditsNeeded;

  const handleClose = () => {
    setSelectedFiles([]);
    setFileValidationErrors({});
    setFileError("");
    closeModal();
  };

  const processFiles = useCallback((files) => {
    const newFiles = Array.from(files);
    const validationErrors = { ...fileValidationErrors }; // Keep existing errors
    let generalError = "";

    newFiles.forEach(file => {
      const error = validateFile(file);
      if (error) {
        validationErrors[file.name] = error;
      }
    });

    // Combine existing and new files, avoiding duplicates by filename
    const existingFileNames = selectedFiles.map(file => file.name);
    const uniqueNewFiles = newFiles.filter(file => !existingFileNames.includes(file.name));
    const combinedFiles = [...selectedFiles, ...uniqueNewFiles];
    
    const totalFiles = combinedFiles.length;
    if (credits !== null && credits < totalFiles) {
        generalError = `Not enough credits. You need ${totalFiles} credit${totalFiles !== 1 ? 's' : ''} but have ${credits}.`;
    }

    setSelectedFiles(combinedFiles);
    setFileValidationErrors(validationErrors);
    setFileError(generalError);

  }, [credits, setFileError, selectedFiles, fileValidationErrors]);

  // Global drag and drop handling
  useEffect(() => {
    const handleGlobalDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (credits !== null && credits < 1) return;
      if (!e.dataTransfer.types.includes('Files')) return;
      
      e.dataTransfer.dropEffect = "copy";
      setDragging(true);
    };

    const handleGlobalDragEnter = (e) => {
      if (credits !== null && credits < 1) return;
      if (!e.dataTransfer.types.includes('Files')) return;
      
      setDragging(true);
    };

    const handleGlobalDragLeave = (e) => {
      // If the drag leaves to outside the window or to an element that
      // is a direct child of the document body, consider it a leave
      if (!e.relatedTarget || e.relatedTarget === document.body) {
        setDragging(false);
      }
    };

    const handleGlobalDragEnd = () => {
      setDragging(false);
    };

    const handleGlobalDrop = () => {
      setDragging(false);
    };

    const handleKeyDown = (e) => {
      // ESC key cancels drag operation
      if (e.key === 'Escape' && dragging) {
        setDragging(false);
      }
    };
    
    // Add global event listeners
    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('dragenter', handleGlobalDragEnter);
    document.addEventListener('dragleave', handleGlobalDragLeave);
    document.addEventListener('dragend', handleGlobalDragEnd);
    document.addEventListener('drop', handleGlobalDrop);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('dragenter', handleGlobalDragEnter);
      document.removeEventListener('dragleave', handleGlobalDragLeave);
      document.removeEventListener('dragend', handleGlobalDragEnd);
      document.removeEventListener('drop', handleGlobalDrop);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [credits, dragging, setDragging]);

  // Local handlers are still needed for the actual drop functionality
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (credits !== null && credits < 1) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      processFiles(droppedFiles);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

   const handleFileChange = (e) => {
      if (credits !== null && credits < 1) return;
      const files = e.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
   };

   const handleTriggerUpload = () => {
      if (!hasValidFiles || !hasEnoughCredits) return;
      onStartBatchUpload(selectedFiles, advancedOptions);
      handleClose();
   }

   const handleRemoveFile = (fileNameToRemove) => {
    setSelectedFiles(prev => prev.filter(f => f.name !== fileNameToRemove));
    setFileValidationErrors(prev => {
      const next = {...prev};
      delete next[fileNameToRemove];
      return next;
    });
    const remainingFiles = selectedFiles.filter(f => f.name !== fileNameToRemove);
    const newCreditsNeeded = remainingFiles.length;
     if (credits !== null && credits < newCreditsNeeded) {
       setFileError(`Not enough credits. You need ${newCreditsNeeded} credit${newCreditsNeeded !== 1 ? 's' : ''} but have ${credits}.`);
     } else if (fileError && fileError.startsWith("Not enough credits")) {
        setFileError("");
     }
   };

  const borderClass = dragging
    ? "border-accent"
    : selectedFiles.length > 0
      ? Object.keys(fileValidationErrors).length > 0 || !hasEnoughCredits
        ? "border-danger"
        : "border-success"
      : credits !== null && credits < 1
        ? "border-border opacity-50"
        : "border-border";

  // Process any initialFiles when the modal opens
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      processFiles(initialFiles);
    }
    // Only run this effect once when the modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      ref={modalDropRef}
    >
      <div
        className={`bg-background-elevated rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col transition-all duration-200 ease-out relative border ${
          dragging ? 'border-accent shadow-accent/20' : 'border-border/60'
        }`}
        style={{ maxHeight: "95vh" }}
        onDrop={(e) => { handleDrop(e); }}
      >
        {/* Drag overlay */}
        {dragging && (
          <div className="absolute inset-0 bg-gradient-to-br from-accent/90 via-primary/80 to-accent/90 rounded-2xl z-10 flex flex-col items-center justify-center backdrop-blur-sm">
            <div className="text-white text-2xl font-bold mb-3">Drop your files here</div>
            <div className="text-white/90 text-sm mb-6">Release to upload</div>
            
            {/* Upload icon */}
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3-3m0 0l3 3m-3-3v12" />
            </svg>
          </div>
        )}
        
        <div className="flex justify-between items-center px-6 py-5 border-b border-border/50 flex-shrink-0">
          <h3 className="text-2xl text-foreground font-bold">Upload files</h3>
          <button
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-background-soft"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {fileError && (
          <div className="mx-6 mt-4 bg-danger-soft border border-danger-border/60 rounded-lg p-4 text-sm text-danger-soft-foreground flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              {fileError}
              {fileError.startsWith("Not enough credits") &&
                 <Link to="/app/billing" className="ml-1 underline text-accent hover:text-accent-hover font-medium">Purchase credits?</Link>
              }
            </div>
          </div>
        )}

        <div className="flex-grow h-[70vh] overflow-y-auto px-6 py-4 space-y-6">
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-base font-semibold text-foreground">
                Choose files
              </label>
              {credits !== null && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${credits > 0 ? 'bg-success-soft text-success-soft-foreground' : 'bg-danger-soft text-danger-soft-foreground'}`}>
                  <div className={`w-2 h-2 rounded-full ${credits > 0 ? 'bg-success' : 'bg-danger'}`} />
                  {credits} credit{credits !== 1 ? 's' : ''} available
                </div>
              )}
            </div>
            <div
              className={`border-2 border-dashed ${borderClass} rounded-xl p-8 flex flex-col items-center justify-center ${(credits !== null && credits < 1) ? "cursor-not-allowed bg-background-soft/50 opacity-60" : "cursor-pointer hover:bg-background-soft/50 hover:border-accent/50"} transition-all duration-200 text-center group`}
              onClick={() => {
                if (credits === null || credits > 0) {
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                ref={fileInputRef}
                id="modal-file-input"
                type="file"
                accept=".glb,.gltf"
                multiple
                disabled={credits !== null && credits < 1}
                onChange={handleFileChange}
                className="hidden"
              />
              {(credits !== null && credits < 1) ? (
                <div className="flex flex-col items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <p className="text-muted-foreground text-sm">
                    No credits available. Purchase more to upload.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-muted-foreground/70 group-hover:text-accent transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div>
                    <p className="text-foreground text-sm font-medium">
                      Drag & drop your files here, or{" "}
                      <span className="text-accent font-semibold cursor-pointer group-hover:underline">
                        click to browse
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground/80 mt-2">Supports .glb and .gltf files • Max {MAX_FILE_SIZE_MB}MB each</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {selectedFiles.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-foreground">Selected files ({selectedFiles.length})</h4>
                <button 
                  onClick={() => { setSelectedFiles([]); setFileValidationErrors({}); setFileError(""); }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear all
                </button>
              </div>
              <ul className="max-h-48 overflow-y-auto bg-background-soft rounded-xl border border-border/60 divide-y divide-border/50">
                {selectedFiles.map((file) => (
                  <li key={file.name} className="flex justify-between items-center p-3 hover:bg-background-elevated transition-colors group">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${fileValidationErrors[file.name] ? 'bg-danger-soft' : 'bg-accent/10'}`}>
                        {fileValidationErrors[file.name] ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-danger" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${fileValidationErrors[file.name] ? 'text-danger' : 'text-foreground'}`} title={file.name}>
                          {file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">{prettyBytes(file.size)}</p>
                        {fileValidationErrors[file.name] && (
                          <p className="text-xs text-danger mt-1">{fileValidationErrors[file.name]}</p>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={() => handleRemoveFile(file.name)} 
                      className="text-muted-foreground hover:text-danger flex-shrink-0 ml-3 p-1 rounded-lg hover:bg-danger-soft/20 transition-all opacity-0 group-hover:opacity-100"
                      aria-label={`Remove ${file.name}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between px-3 py-2 bg-background-soft/50 rounded-lg border border-border/40">
                <span className="text-xs text-muted-foreground">Total size: {prettyBytes(totalSelectedSize)}</span>
                <span className="text-xs font-medium text-foreground">Credits needed: {creditsNeeded}</span>
              </div>
            </div>
          )}

           <PresetSelector
             selectedPreset={selectedPreset}
             presets={presets}
             onSelect={onPresetSelect}
             isLoading={presetsLoading}
             error={presetsError}
             className="pt-2"
           />

          {(credits === null || credits > 0) && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-accent hover:text-accent-hover focus:outline-none focus:underline font-medium transition-colors flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {showAdvanced ? "Hide compression options" : "Show compression options"}
              </button>
            </div>
          )}

          {showAdvanced && (credits === null || credits > 0) && (
              <AdvancedOptions options={advancedOptions} onNestedChange={onNestedChange} />
          )}
        </div>

        <div className="px-6 py-4 border-t border-border/50 flex justify-end gap-3 flex-shrink-0 bg-background-soft/30">
           <Button
             variant="secondary"
             onClick={handleClose}
           >
             Cancel
           </Button>
           {(credits !== null && credits < 1 && selectedFiles.length === 0) ? (
             <Link
               to="/app/billing"
               className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm"
             >
               Purchase credits
             </Link>
           ) : (
             <Button
               disabled={!hasValidFiles || !hasEnoughCredits}
               onClick={handleTriggerUpload}
               className="min-w-[140px]"
               title={!hasValidFiles ? "Select valid .glb or .gltf files to upload" : !hasEnoughCredits ? `Not enough credits (need ${creditsNeeded})` : `Upload ${selectedFiles.length} file(s)`}
             >
               <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
               </svg>
               Upload {selectedFiles.length > 0 ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}` : 'files'}
             </Button>
           )}
        </div>
      </div>
    </div>
  );
} 